import { OpenGovAppConfig } from '@opengov/app-config';
import { transport } from '@opengov/app-core/transport';
import {
  DEFAULT_FONT,
  STATE_DRAFT,
  STATE_INTERNAL_PUBLISHED,
  STATE_PUBLISHED
} from '@opengov/stories-shared/constants';
import { upsertInternalStory, removeInternalPublishedStory, checkStoryTitleExistsQuery } from '@opengov/stories-shared/graphQL';
import { IStory, IStoryContent, ProgressTrackerSettingType } from '@opengov/stories-shared/types';
import { generateHTML, getFullPageContent } from '@opengov/stories-shared/utils';

import { ICreateStoryOrTemplatePayload, IGetStoryGraphqlResponse, ISettings } from '../../types';
import {
  createTemplate,
  getStoryById,
  getStoryEmbeds,
  getTemplateById,
  getUserByOgId,
  insertStoryEmbeds,
  insertStoryEmbedsWithEmbedData,
  removePortalStory,
  removeStoryContent,
  removeStoryEmbeds,
  removeTemplate,
  removeTemplateContent,
  updatePublication,
  updateStory,
  updateTemplate
} from '../graphQL/queries';
import { processStoryOrTemplate } from '../transformations';
import { postQuery } from '../utils';

export type PublishStoryParams = {
  sortedStoryContents: IStoryContent[];
  settings: ISettings;
  isTemplate: boolean;
  hasGallery: boolean;
  userId: number;
  story: IStory;
  storyCustomCss?: string;
  publicationCustomCss?: string;
  entityUuid?: string;
  progressTrackerSettings?: ProgressTrackerSettingType[];
};

export const getStoryOrTemplateById = async (isTemplate: boolean, storyId: string): Promise<IStory> => {
  return postQuery({ query: isTemplate ? getTemplateById : getStoryById, variables: { id: storyId } }).then(
    (response) => {
      const resp = isTemplate
        ? (response.data.resource as IGetStoryGraphqlResponse)
        : (response.data.resource[0] as IGetStoryGraphqlResponse);
      const graphqlResponse: IGetStoryGraphqlResponse = resp;
      return processStoryOrTemplate(graphqlResponse);
    }
  );
};

export const updateStoryOrTemplate = async (
  isTemplate: boolean,
  storyId: string,
  variables: Partial<IStory>
): Promise<IStory> => {
  // Remove isFollowable from variables if updating a template
  const filteredVariables = isTemplate
    ? (({ isFollowable: _isFollowable, ...rest }) => rest)(variables)
    : variables;

  return postQuery({
    query: isTemplate ? updateTemplate : updateStory,
    variables: { id: storyId, data: filteredVariables }
  }).then(async (response) => {
    return processStoryOrTemplate(response.data.resource) as IStory;
  });
};

export const updatePublicationById = async (
  publicationId: string,
  variables: any
): Promise<{ id: string; publicUrl: string }> => {
  return postQuery({
    query: updatePublication,
    variables: { id: publicationId, data: variables }
  }).then(async (response) => {
    return response;
  });
};

export const removeEmbeds = async (storyId: string) => {
  return postQuery({ query: removeStoryEmbeds, variables: { storyId } });
};

export const insertEmbeds = async (data: any) => {
  return postQuery({ query: insertStoryEmbeds, variables: { data } });
};

export const insertEmbedsWithEmbedData = async (data: any) => {
  return postQuery({ query: insertStoryEmbedsWithEmbedData, variables: { data } });
};

export const getStoryEmbed = async (storyId: string) => {
  return postQuery({ query: getStoryEmbeds, variables: { storyId } }).then(async (response) => {
    return response.data.storyEmbeds;
  });
};

export const checkStoryTitleExists = async (title: string, entityId: number, folderId: string): Promise<boolean> => {
  return postQuery({ query: checkStoryTitleExistsQuery, variables: { title, entityId, folderId } }).then(async (response): Promise<boolean> => {
    return response.data.stories_aggregate.aggregate.count > 0;
  });
};

export const unpublishExternalStory = async (story: IStory): Promise<IStory> => {
  postQuery({ query: removePortalStory, variables: { id: story._id } })
    .then((res) => res)
    .catch(() => { });
  return transport
    .url(`/api/stories/v1/stories/remove`)
    .json({ story })
    .post()
    .json()
    .then(async () => {
      const variables = {
        state: STATE_DRAFT as IStory['state'],
        publicUrl: null,
        publishedAt: null,
        publishedBy: null,
        isFollowable: false
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return updateStoryOrTemplate(false, story._id, variables as any);
    });
};

export const unpublishInternalStory = async (story: IStory): Promise<IStory> => {
  return postQuery({
    query: removeInternalPublishedStory,
    variables: { id: story._id }
  }).then(async () => {
    const variables = {
      state: STATE_DRAFT as IStory['state'],
      publishedAt: null,
      publishedBy: null,
      isFollowable: false
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return updateStoryOrTemplate(false, story._id, variables as any);
  });
};

export const deleteStoryContent = async (isTemplate: boolean, storyContentId: string): Promise<any> => {
  return postQuery({
    variables: {
      storyContentId
    },
    query: isTemplate ? removeTemplateContent : removeStoryContent
  });
};

export const getUserById = async (id: number): Promise<string> => {
  return postQuery({ query: getUserByOgId, variables: { id } }).then(async (response) => {
    return response.data.storyUsers[0]._id;
  });
};

export const createTemplateFromStory = async (
  payload: Partial<ICreateStoryOrTemplatePayload>,
  userId: number
): Promise<IStory> => {
  const authorId = await getUserById(userId);
  const data = { ...payload, authorId: authorId, createdBy: authorId };
  return postQuery({ query: createTemplate, variables: { data } }).then(async (response) => {
    return processStoryOrTemplate(response.data.template);
  });
};

export const deleteTemplate = async (id: string): Promise<any> => {
  return postQuery({ query: removeTemplate, variables: { id } });
};

export const publishExternalStory = async ({
  sortedStoryContents,
  settings,
  isTemplate,
  hasGallery,
  userId,
  story,
  storyCustomCss,
  publicationCustomCss,
  entityUuid
}: PublishStoryParams): Promise<IStory> => {
  if (story.state === `internalPublished`) {
    await unpublishInternalStory(story);
  }
  return transport
    .url(`/api/stories/v1/stories/upload`)
    .json({
      story,
      sortedStoryContents,
      settings,
      hasGallery: hasGallery || false,
      storyCustomCss,
      publicationCustomCss,
      entityUuid
    })
    .post()
    .json()
    .then(async (url) => {
      const id = await getUserById(userId);
      const variables = {
        state: STATE_PUBLISHED as IStory['state'],
        publishedAt: new Date().toISOString(),
        publishedBy: id,
        publicUrl: `${OpenGovAppConfig.STORIES_URL}/${url.data}`,
        isFollowable: story.isFollowable
      };
      return updateStoryOrTemplate(isTemplate, story._id, variables);
    });
};

export type ShareOnlinePublicationParams = {
  publicationId: string;
  entitySubdomain: string;
  publishedId: string;
  userId: number;
  enableGoogleTranslateForPublications: boolean;
  isPreview?: boolean;
  /** Set only for user-initiated publishes so Stories admins get an in-app notification. */
  notifyAdmins?: boolean;
};
export const shareOnlinePublication = async ({
  publicationId,
  entitySubdomain,
  publishedId,
  userId,
  enableGoogleTranslateForPublications,
  isPreview = false,
  notifyAdmins = false
}: ShareOnlinePublicationParams): Promise<string> => {
  return transport
    .url(`/api/stories/v1/onlinePublication/upload`)
    .json({
      publicationId,
      entitySubdomain,
      publishedId,
      enableGoogleTranslateForPublications,
      isPreview,
      notifyAdmins
    })
    .post()
    .json()
    .then(async (url) => {
      if (!isPreview) {
        const id = await getUserById(userId);
        const variables = {
          publishedAt: new Date(),
          publishedBy: id,
          publicUrl: `${OpenGovAppConfig.STORIES_URL}/${url.data}`,
          status: 'published'
        };
        await updatePublicationById(publicationId, variables);
      }
      return `${OpenGovAppConfig.STORIES_URL}/${url.data}`;
    })
    .catch((err) => {
      return err;
    });
};

export const deactivateSharedOnlinePublication = async (
  publicationId: string,
  entitySubdomain: string,
  entityId: number,
  publishedId: string
): Promise<string> => {
  return transport
    .url(`/api/stories/v1/onlinePublication/remove`)
    .json({
      publicationId,
      entitySubdomain,
      entityId,
      publishedId
    })
    .post()
    .json()
    .then(async () => {
      return publicationId;
    });
};

export const publishInternalStory = async ({
  sortedStoryContents,
  settings,
  isTemplate,
  hasGallery,
  userId,
  story,
  storyCustomCss,
  publicationCustomCss,
  progressTrackerSettings = []
}: PublishStoryParams) => {
  const isStoryWithProgressTracker = sortedStoryContents.some((storyContent) => Array.isArray(storyContent.contentChartConfig) && storyContent.contentChartConfig.length > 0);
  const { content, containers } = getFullPageContent(sortedStoryContents, isStoryWithProgressTracker, story.entity_id, progressTrackerSettings);

  const html = generateHTML({
    id: story._id,
    title: story.title,
    description: story.description || '',
    thumbnail_relative_path: story.thumbnail_relative_path,
    fonts: [settings.brand.fonts] || [
      {
        headline: DEFAULT_FONT.id,
        paragraph: DEFAULT_FONT.id
      }
    ],
    content,
    hasGallery,
    options: {
      showSocialButtons: true,
      gaTrackingCode: settings.advanced.google_analytics_id || ''
    },
    storyCustomCss,
    publicationCustomCss,
    isFollowable: false,
    containers,
    isStoryWithProgressTracker
  });
  const internalStory = { storyId: story._id, contentHtml: html };

  if (story.state === `published`) {
    await unpublishExternalStory(story);
  }
  return postQuery({ query: upsertInternalStory, variables: { internalStory } })
    .then(async () => {
      const id = await getUserById(userId);
      const variables = {
        state: STATE_INTERNAL_PUBLISHED as IStory['state'],
        publishedAt: new Date().toISOString(),
        publishedBy: id,
        publicUrl: '',
        isFollowable: false
      };
      return updateStoryOrTemplate(isTemplate, story._id, variables);
    });
};
